import { Component, effect, inject, OnDestroy, signal } from '@angular/core';
import { NonNullableFormBuilder, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { FormValidators } from '@corelabs/angular/forms';
import { ButtonModule } from 'primeng/button';
import { FormInputComponent, FormTextareaComponent } from '@shared/forms';
import { ModuleFacade } from '@app/store/module';
import { FormOptionsFacade } from '@app/store/form-options';
import { ItemSelection, ItemSelectionService } from '@app/dialogs';
import { ItemListComponent } from '../item-list/item-list.component';

@Component({
    selector: 'app-module-add-modal',
    templateUrl: './add.component.html',
    styleUrls: ['./add.component.css'],
    imports: [ReactiveFormsModule, TranslateModule, ButtonModule, FormInputComponent, FormTextareaComponent, ItemListComponent],
})
export class AddComponent implements OnDestroy {
    private readonly router = inject(Router);
    private readonly route = inject(ActivatedRoute);
    private readonly moduleFacade = inject(ModuleFacade);
    private readonly formOptionsFacade = inject(FormOptionsFacade);
    private readonly formBuilder = inject(NonNullableFormBuilder);
    private readonly itemSelectionService = inject(ItemSelectionService);

    readonly saving = this.moduleFacade.saving;

    readonly selectedItems = signal<ItemSelection[]>([]);

    readonly form = this.formBuilder.group({
        name: this.formBuilder.control('', [FormValidators.Required, FormValidators.Text]),
        description: this.formBuilder.control(''),
    });

    constructor() {
        effect(() => {
            if (this.moduleFacade.saved()) {
                this.close();
            }
        });

        effect(() => {
            if (this.moduleFacade.saving() === false && this.moduleFacade.saved() === false) {
                this.form.enable();
            }
        });

        effect(() => {
            const selection = this.itemSelectionService.selected();
            if (selection) {
                this.selectedItems.update((items) => {
                    if (items.some((i) => i.id === selection.id && i.type === selection.type)) {
                        return items;
                    }
                    return [...items, selection];
                });
                this.itemSelectionService.clear();
            }
        });
    }

    ngOnDestroy(): void {
        this.moduleFacade.clear();
        this.formOptionsFacade.clear();
    }

    openItemDialog(): void {
        this.router.navigate([{ outlets: { dialog: ['select-item'] } }]);
    }

    removeItem(item: ItemSelection): void {
        this.selectedItems.update((items) => items.filter((i) => !(i.id === item.id && i.type === item.type)));
    }

    reorderItems(items: ItemSelection[]): void {
        this.selectedItems.set(items);
    }

    onSubmit(): void {
        this.form.markAllAsTouched();

        if (this.form.valid) {
            this.form.disable();
            const value = this.form.getRawValue();
            this.moduleFacade.createModule({
                name: value.name,
                description: value.description || undefined,
                items: this.selectedItems().map((i) => ({ type: i.type, id: i.id })),
            });
        }
    }

    close(): void {
        this.router.navigate([{ outlets: { modal: null } }]);
    }
}
